import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCurrency } from './CurrencyContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('vedaVineCart');
      if (savedCart && savedCart !== 'undefined') {
        return JSON.parse(savedCart);
      }
    } catch (e) {
      console.error('Error parsing cart data:', e);
      localStorage.removeItem('vedaVineCart');
    }
    return [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('vedaVineCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1, variant = null) => {
    setCartItems((prevItems) => {
      // Create unique ID combining product and variant
      const itemId = variant ? `${product.id}-${variant._id}` : product.id;
      const existingItem = prevItems.find((item) => item.itemId === itemId);
      
      if (existingItem) {
        return prevItems.map((item) =>
          item.itemId === itemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      // Add new item with variant info
      const newItem = {
        ...product,
        itemId,
        quantity,
        selectedVariant: variant,
        price: variant ? variant.sellingPrice : product.price,
        mrp: variant ? variant.mrp : product.mrp,
      };
      
      return [...prevItems, newItem];
    });
  };

  const removeFromCart = (itemId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.itemId !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.itemId === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  };

  const getFormattedCartTotal = () => {
    const total = getCartTotal();

    if (!cartItems.length) return "0.00";

    return `${cartItems[0].selectedVariant.currencySymbol}${total.toFixed(2)}`;
  };


  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getFormattedCartTotal,
    getCartCount,
    isCartOpen,
    setIsCartOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
